import { DoesFilterPassParams } from "ag-grid-enterprise";
import { DateUtils } from "utils/dateutils";
import { BLANKS_FILTER_KEY, LEGACY_BLANK_DATE_KEYS } from "constants/index";

const dateFormat = "MM/DD/YYYY";

/**
 * "(Blanks)" plus the pre-CIQ-12252 sentinels still living in saved views and in
 * the dashboard drill-downs. All of them mean "this row has no date".
 */
const isBlanksKey = (key: string) =>
  key === BLANKS_FILTER_KEY || LEGACY_BLANK_DATE_KEYS.includes(key);

type TModelDateType = {
  values: string[];
  filterType: string;
  type?: string;
  dateFrom?: string;
  dateTo?: string;
};

function valueInRange(
  value: unknown,
  cell: number,
  from: number,
  to: number,
  inclusiveEnd = false
): boolean {
  if (!value) return false;
  if (cell < from) return false;
  return inclusiveEnd ? cell <= to : cell < to;
}

function matchesNamedDatePreset(
  selectedFilter: string,
  value: unknown,
  cellDateValue: number,
  todayDateValue: number,
  model: TModelDateType | null | undefined
): boolean {
  switch (selectedFilter) {
    case "Past today":
      return todayDateValue > cellDateValue && Boolean(value);
    case "Today":
      return (
        DateUtils.dateTimeObj(value as string).format(dateFormat) ===
          DateUtils.dateTimeObj().format(dateFormat) && Boolean(value)
      );
    case "Next 3 days":
      return (
        Boolean(value) &&
        cellDateValue > todayDateValue &&
        cellDateValue <=
          DateUtils.dateTimeObj(todayDateValue).add(4, "days").valueOf()
      );
    case "Next 2 weeks":
      return (
        Boolean(value) &&
        cellDateValue > todayDateValue &&
        cellDateValue <=
          DateUtils.dateTimeObj(todayDateValue).add(15, "days").valueOf()
      );
    case "Past 7 days":
      return valueInRange(
        value,
        cellDateValue,
        DateUtils.dateTimeObj(todayDateValue).subtract(6, "days").valueOf(),
        DateUtils.dateTimeObj(todayDateValue).add(1, "days").valueOf()
      );
    case "Last 30 days":
      return valueInRange(
        value,
        cellDateValue,
        DateUtils.dateTimeObj(todayDateValue).subtract(29, "days").valueOf(),
        DateUtils.dateTimeObj(todayDateValue).add(1, "days").valueOf()
      );
    case "This month":
      return monthWindow(value, cellDateValue, todayDateValue);
    case "This quarter":
      return quarterWindow(value, cellDateValue, todayDateValue);
    case "Current year":
      return yearWindow(value, cellDateValue, todayDateValue);
    case "CustomDateRange":
      return (
        cellDateValue >= DateUtils.dateTimeObj(model?.dateFrom).valueOf() &&
        cellDateValue <= DateUtils.dateTimeObj(model?.dateTo).valueOf()
      );
    case "Overdue":
      return cellDateValue < todayDateValue;
    case "Due in 5 days": {
      const from = DateUtils.dateTimeObj(
        DateUtils.dateTimeObj().format(dateFormat)
      ).valueOf();
      const to = DateUtils.dateTimeObj(
        DateUtils.dateTimeObj().format(dateFormat)
      )
        .add(6, "d")
        .valueOf();
      return cellDateValue >= from && cellDateValue <= to;
    }
    case "Overdue or due in 2 weeks":
      return (
        cellDateValue <
        DateUtils.dateTimeObj(todayDateValue).add(15, "days").valueOf()
      );
    case "due after 2 weeks":
      return (
        Boolean(value) &&
        cellDateValue >=
          DateUtils.dateTimeObj(todayDateValue).add(15, "days").valueOf()
      );
    case "after date":
      return afterDate(value, cellDateValue, model?.dateFrom);
    default:
      return false;
  }
}

function monthWindow(
  value: unknown,
  cellDateValue: number,
  todayDateValue: number
): boolean {
  const start = DateUtils.dateTimeObj(todayDateValue).startOf("month");
  return valueInRange(
    value,
    cellDateValue,
    start.valueOf(),
    start.add(1, "month").valueOf()
  );
}

function quarterWindow(
  value: unknown,
  cellDateValue: number,
  todayDateValue: number
): boolean {
  const anchor = DateUtils.dateTimeObj(todayDateValue);
  const start = anchor
    .month(anchor.month() - (anchor.month() % 3))
    .startOf("month");
  return valueInRange(
    value,
    cellDateValue,
    start.valueOf(),
    start.add(3, "month").valueOf()
  );
}

function yearWindow(
  value: unknown,
  cellDateValue: number,
  todayDateValue: number
): boolean {
  const start = DateUtils.dateTimeObj(todayDateValue).startOf("year");
  return valueInRange(
    value,
    cellDateValue,
    start.valueOf(),
    start.add(1, "year").valueOf()
  );
}

function afterDate(
  value: unknown,
  cellDateValue: number,
  dateFrom: string | undefined
): boolean {
  if (!dateFrom) return false;
  return (
    Boolean(value) && cellDateValue >= DateUtils.dateTimeObj(dateFrom).valueOf()
  );
}

export const doesFilterPass: ({
  model,
  node,
  handlerParams
}: DoesFilterPassParams<any, any, TModelDateType>) => boolean = ({
  model,
  node,
  handlerParams
}: DoesFilterPassParams<any, any, TModelDateType>) => {
  const value = handlerParams.getValue(node);
  const selectedFilters = model?.values?.length ? model.values : [];

  if (selectedFilters.length === 0) return false;

  const cellDateValue = value ? DateUtils.dateTimeObj(value).valueOf() : 0;
  const todayDateValue = DateUtils.dateTimeObj(
    DateUtils.format(DateUtils.dateTimeObj())
  ).valueOf();

  return selectedFilters.some((selectedFilter) => {
    // Only the blank-matching options and the range check may see an empty cell.
    if (
      !value &&
      selectedFilter !== "CustomDateRange" &&
      !isBlanksKey(selectedFilter)
    ) {
      return false;
    }

    // Matches null/undefined/"" and any unparseable date, which is the
    // pre-existing semantics of the "Actual Date NA" case this replaces.
    if (isBlanksKey(selectedFilter)) return !cellDateValue;
    return matchesNamedDatePreset(
      selectedFilter,
      value,
      cellDateValue,
      todayDateValue,
      model
    );
  });
};
